import { useState } from 'react';
import { estimateCosts } from '../services/apiService';

export default function CostEstimator() {
  // Default values based on Gemini 2.5 Flash pricing (per 1M tokens):
  // Input: $0.30 per 1M tokens
  // Output: $2.50 per 1M tokens
  const [costPer1MInputTokens, setCostPer1MInputTokens] = useState<string>('0.30');
  const [costPer1MOutputTokens, setCostPer1MOutputTokens] = useState<string>('2.50');
  const [overagePricePerInteraction, setOveragePricePerInteraction] = useState<string>('0.10');
  const [loading, setLoading] = useState(false);
  const [estimates, setEstimates] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCalculate = async () => {
    setLoading(true);
    setError(null);
    setEstimates(null);

    try {
      const inputCostPer1M = parseFloat(costPer1MInputTokens);
      const outputCostPer1M = parseFloat(costPer1MOutputTokens);
      const overagePrice = parseFloat(overagePricePerInteraction);

      if (isNaN(inputCostPer1M) || isNaN(outputCostPer1M) || isNaN(overagePrice)) {
        setError('Please enter valid numbers for all cost fields');
        setLoading(false);
        return;
      }

      // Convert from per-1M-token cost to per-token cost (divide by 1,000,000)
      const costPerInputToken = inputCostPer1M / 1000000;
      const costPerOutputToken = outputCostPer1M / 1000000;

      const result = await estimateCosts(null, costPerInputToken, costPerOutputToken, overagePrice);
      setEstimates(result.estimates);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate costs');
    } finally {
      setLoading(false);
    }
  };

  const totalEstimatedCost = estimates?.reduce((sum, est) => sum + (est.estimatedCost || 0), 0) || 0;
  const totalEstimatedOveragePrice = estimates?.reduce((sum, est) => sum + (est.estimatedOveragePrice || 0), 0) || 0;

  return (
    <div className="bg-white p-6 rounded-lg border border-neutral-border">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">Cost Estimation</h3>
        <a
          href="https://ai.google.dev/gemini-api/docs/pricing#gemini-2.5-flash"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-brand-primary hover:underline"
        >
          Google Gemini API Pricing →
        </a>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-neutral-secondary mb-2">
            Cost per 1M Input Tokens (USD)
          </label>
          <input
            type="number"
            step="0.01"
            value={costPer1MInputTokens}
            onChange={(e) => setCostPer1MInputTokens(e.target.value)}
            className="w-full px-4 py-2 border border-neutral-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            placeholder="0.30"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-secondary mb-2">
            Cost per 1M Output Tokens (USD)
          </label>
          <input
            type="number"
            step="0.01"
            value={costPer1MOutputTokens}
            onChange={(e) => setCostPer1MOutputTokens(e.target.value)}
            className="w-full px-4 py-2 border border-neutral-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            placeholder="2.50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-secondary mb-2">
            Overage Price per Interaction (USD)
          </label>
          <input
            type="number"
            step="0.01"
            value={overagePricePerInteraction}
            onChange={(e) => setOveragePricePerInteraction(e.target.value)}
            className="w-full px-4 py-2 border border-neutral-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            placeholder="0.10"
          />
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={loading}
        className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 mb-4"
      >
        {loading ? 'Calculating...' : 'Calculate Estimated Costs'}
      </button>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {estimates && (
        <div>
          <div className="mb-4 p-4 bg-blue-50 rounded-lg grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-neutral-secondary">Total Estimated Token Cost</p>
              <p className="text-2xl font-bold text-brand-primary">${totalEstimatedCost.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-secondary">Total Estimated Overage Price</p>
              <p className="text-2xl font-bold text-brand-primary">${totalEstimatedOveragePrice.toFixed(2)}</p>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Input Tokens</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Output Tokens</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Input Token Cost</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Output Token Cost</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Total Cost</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Non-Overage Interactions</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Overage Interactions</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-secondary uppercase">Estimated Overage Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-border">
                {estimates
                  .sort((a, b) => {
                    const totalA = (a.estimatedCost || 0) + (a.estimatedOveragePrice || 0);
                    const totalB = (b.estimatedCost || 0) + (b.estimatedOveragePrice || 0);
                    return totalB - totalA;
                  })
                  .map((est) => {
                    // Convert from per-1M-token cost to per-token cost
                    const inputCostPer1M = parseFloat(costPer1MInputTokens);
                    const outputCostPer1M = parseFloat(costPer1MOutputTokens);
                    const costPerInputToken = inputCostPer1M / 1000000;
                    const costPerOutputToken = outputCostPer1M / 1000000;
                    
                    const inputTokenCost = (est.inputTokens || 0) * costPerInputToken;
                    const outputTokenCost = (est.outputTokens || 0) * costPerOutputToken;
                    const totalCost = inputTokenCost + outputTokenCost;
                    
                    return (
                      <tr key={est.userId} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-neutral-primary">{est.email || ''}</td>
                        <td className="px-4 py-2 text-sm text-neutral-primary">{(est.inputTokens || 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm text-neutral-primary">{(est.outputTokens || 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm text-neutral-primary">${inputTokenCost.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-neutral-primary">${outputTokenCost.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm font-bold text-brand-primary">${totalCost.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-neutral-primary">{(est.nonOverageInteractions || 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm text-neutral-primary">{(est.overageInteractions || 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm font-medium text-neutral-primary">${(est.estimatedOveragePrice || 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
